import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Trash2, Pencil } from 'lucide-react';
import ErrorModal from '@/components/ErrorModal';

export default function Stadiums() {
  const [stadiums, setStadiums] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', city: '', country: '' });
  const [loading, setLoading] = useState(true);

  // Error modal state
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: 'Error',
    message: ''
  });

  const load = () => { api.get('/stadiums').then(r => setStadiums(r.data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const handleSave = async () => {
    if (!form.name || !form.city) { 
      setErrorModal({
        open: true,
        title: 'Campos requeridos faltantes',
        message: 'Por favor, completa los campos: Nombre del estadio y Ciudad.'
      });
      return; 
    }
    try {
      if (editing) { await api.put(`/stadiums/${editing}`, form); toast.success('Estadio actualizado'); }
      else { await api.post('/stadiums', form); toast.success('Estadio creado'); }
      setShowForm(false); setEditing(null); setForm({ name: '', city: '', country: '' }); load();
    } catch (err) { 
      const errorMessage = err.response?.data?.detail || 'Error al guardar estadio';
      setErrorModal({
        open: true,
        title: editing ? 'Error al actualizar estadio' : 'Error al crear estadio',
        message: errorMessage
      });
    }
  };

  const handleEdit = (s) => { setEditing(s.id); setForm({ name: s.name, city: s.city, country: s.country }); setShowForm(true); };
  const handleDelete = async (id) => { if (!window.confirm('Eliminar estadio?')) return; await api.delete(`/stadiums/${id}`); toast.success('Estadio eliminado'); load(); };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="admin-stadiums-page">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-black gold-text text-center lg:text-left" style={{ fontFamily: 'Montserrat' }}>Estadios</h2>
        <button onClick={() => { setEditing(null); setForm({ name: '', city: '', country: '' }); setShowForm(true); }} className="gold-btn px-4 py-2 rounded-lg text-sm flex items-center gap-2" data-testid="create-stadium-button">
          <Plus size={16} /> Crear
        </button>
      </div>

      {loading ? <div className="text-center text-white/50 py-10">Cargando...</div> : stadiums.length === 0 ? (
        <div className="glass-card p-8 text-center text-white/40">No hay estadios registrados</div>
      ) : (
        <div className="space-y-2">
          {stadiums.map(s => (
            <div key={s.id} className="glass-card p-4 flex items-center justify-between" data-testid={`stadium-row-${s.id}`}>
              <div>
                <div className="text-white font-semibold text-sm">{s.name}</div>
                <div className="text-white/40 text-xs">{s.city}, {s.country}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleEdit(s)} className="p-2 rounded-lg hover:bg-white/10 text-white/50 hover:text-[#F4E39E]"><Pencil size={16} /></button>
                <button onClick={() => handleDelete(s.id)} className="p-2 rounded-lg hover:bg-red-500/10 text-white/50 hover:text-red-400"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-[#111A30] border-[#F4E39E]/30 text-white max-w-sm">
          <DialogHeader><DialogTitle className="text-[#F4E39E]" style={{ fontFamily: 'Montserrat' }}>{editing ? 'Editar Estadio' : 'Crear Estadio'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-white/60 text-sm">Nombre del estadio</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="stadium-name-input"
                className="w-full bg-[#0A0F1C] border border-[#F4E39E]/40 rounded-lg px-4 py-3 text-white mt-1 focus:border-[#F4E39E] outline-none" placeholder="Estadio Azteca" />
            </div>
            <div>
              <label className="text-white/60 text-sm">Ciudad</label>
              <input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} data-testid="stadium-city-input"
                className="w-full bg-[#0A0F1C] border border-[#F4E39E]/40 rounded-lg px-4 py-3 text-white mt-1 focus:border-[#F4E39E] outline-none" placeholder="Ciudad de Mexico" />
            </div>
            <div>
              <label className="text-white/60 text-sm">Pais</label>
              <input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} data-testid="stadium-country-input"
                className="w-full bg-[#0A0F1C] border border-[#F4E39E]/40 rounded-lg px-4 py-3 text-white mt-1 focus:border-[#F4E39E] outline-none" placeholder="Mexico" />
            </div>
            <button onClick={handleSave} className="gold-btn w-full py-3 rounded-lg font-bold" data-testid="save-stadium-button">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error Modal */}
      <ErrorModal
        open={errorModal.open}
        onClose={() => setErrorModal({ ...errorModal, open: false })}
        title={errorModal.title}
        message={errorModal.message}
      />
    </div>
  );
}
